import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash, FileVideo, FilePdf, Exam } from "@phosphor-icons/react";
import { toast } from "sonner";

export function CourseBuilder() {
  const [modules, setModules] = useState<any[]>([]);
  const [courseTitle, setCourseTitle] = useState("");

  const addModule = (type: string) => {
    setModules([...modules, { id: Date.now(), title: "", type }]);
  };

  const removeModule = (id: number) => {
    setModules(modules.filter(m => m.id !== id));
  };

  const handleSave = () => {
    if (!courseTitle) {
      toast.error("Please enter a course title");
      return;
    }
    toast.success("Course saved as draft!");
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Course Details</CardTitle>
            <CardDescription>Basic information about your course</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Course Title</Label>
              <Input 
                placeholder="e.g. Advanced Leadership" 
                value={courseTitle}
                onChange={(e) => setCourseTitle(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea placeholder="What will students learn?" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Course Content</CardTitle>
            <CardDescription>Add modules, videos, and quizzes</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {modules.map((module, index) => (
              <div key={module.id} className="border rounded-lg p-4 flex items-center gap-4 bg-slate-50 dark:bg-slate-900/50">
                <div className="h-8 w-8 rounded-full bg-slate-200 dark:bg-slate-800 flex items-center justify-center font-bold text-slate-500">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {module.type === 'video' && <FileVideo className="text-blue-500" />}
                    {module.type === 'pdf' && <FilePdf className="text-red-500" />}
                    {module.type === 'quiz' && <Exam className="text-purple-500" />}
                    <span className="text-xs uppercase font-bold text-slate-400">{module.type}</span>
                  </div>
                  <Input 
                    placeholder="Module Title" 
                    value={module.title} 
                    onChange={(e) => {
                      const newModules = [...modules];
                      newModules[index].title = e.target.value;
                      setModules(newModules);
                    }}
                  />
                </div>
                <Button variant="ghost" size="icon" className="text-red-500" onClick={() => removeModule(module.id)}>
                  <Trash />
                </Button>
              </div>
            ))}

            {modules.length === 0 && (
              <div className="text-center py-8 border-2 border-dashed rounded-lg text-slate-400">
                No content yet. Add modules below.
              </div>
            )}

            <div className="flex gap-2 justify-center mt-4">
              <Button variant="outline" onClick={() => addModule('video')}>
                <Plus className="mr-2" /> Add Video
              </Button>
              <Button variant="outline" onClick={() => addModule('pdf')}>
                <Plus className="mr-2" /> Add PDF
              </Button>
              <Button variant="outline" onClick={() => addModule('quiz')}>
                <Plus className="mr-2" /> Add Quiz
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Publishing</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button className="w-full" onClick={handleSave}>Save Draft</Button>
            <Button variant="outline" className="w-full">Preview</Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
